import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, HostListener, PLATFORM_ID, Inject, Output, EventEmitter } from '@angular/core';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import * as moment from 'moment';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit {
  
  @ViewChild(DaterangepickerDirective) rangePickerDir!: DaterangepickerDirective;
  @ViewChild('rangeActions') rangeActions!: ElementRef<HTMLDivElement>;

  public selected: any;
  @Output() public applyRange = new EventEmitter<any>();
  public ranges: any;
  public pickerOpen = false;

  constructor(
    private cdr: ChangeDetectorRef, 
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    const m: any = moment;
    this.selected = this.selected || { start: m(), end: m() };
    this.ranges = {
      'Today': [m(), m()],
      'Yesterday': [m().subtract(1, 'days'), m().subtract(1, 'days')],
      'This Week': [m().startOf('week'), m().endOf('day')],
      'Last Week': [m().subtract(1, 'week').startOf('week'), m().subtract(1, 'week').endOf('week')],
      'This Month': [m().startOf('month'), m().endOf('day')],
      'Last Month': [m().subtract(1, 'month').startOf('month'), m().subtract(1, 'month').endOf('month')]
    };
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    setTimeout(() => this.syncPickerOpenState(), 0);
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.pickerOpen) {
      this.positionPickerInViewport();
    }
  }
  
  syncPickerOpenState(): void {
    const open = !!(this.rangePickerDir && this.rangePickerDir.picker && this.rangePickerDir.picker.isShown);
    if (open) {
      setTimeout(() => {
        this.attachActionsToPicker();
        this.pickerOpen = true;
        this.cdr.detectChanges();
        this.positionPickerInViewport();
        // Library setPosition + footer paint: re-fit into viewport
        setTimeout(() => this.positionPickerInViewport(), 50);
      }, 0);
      return;
    }
    this.pickerOpen = false;
    this.cdr.detectChanges();
  }

  attachActionsToPicker(): void {
    if (!isPlatformBrowser(this.platformId) || !this.rangeActions) {
      return;
    }
    const picker = document.querySelector('app-flow-tracking .md-drppicker.shown') as HTMLElement | null;
    const actions = this.rangeActions.nativeElement;
    if (picker && actions && actions.parentElement !== picker) {
      picker.appendChild(actions);
    }
  }

  /** Keep picker in the viewport with position:fixed so .main-wrapper overflow cannot clip Apply/Cancel. */
  positionPickerInViewport(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const input = document.getElementById('flow_tracking_range') as HTMLElement | null;
    const picker = document.querySelector('app-flow-tracking .md-drppicker.shown') as HTMLElement | null;
    if (!input || !picker) {
      return;
    }

    const gap = 6;
    const margin = 8;
    const rect = input.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Measure after forcing fixed so layout is stable
    picker.style.setProperty('position', 'fixed', 'important');
    picker.style.setProperty('transform', 'none', 'important');
    picker.style.setProperty('margin', '0', 'important');

    const pickerWidth = Math.min(picker.offsetWidth || 640, vw - margin * 2);
    const spaceBelow = vh - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const preferDown = spaceBelow >= 280 || spaceBelow >= spaceAbove;
    const available = Math.max(180, preferDown ? spaceBelow : spaceAbove);
    const maxHeight = Math.min(640, available);

    picker.style.setProperty('max-height', `${maxHeight}px`, 'important');
    picker.style.setProperty('overflow-x', 'auto', 'important');
    picker.style.setProperty('overflow-y', 'auto', 'important');
    picker.style.setProperty('width', 'auto', 'important');
    picker.style.setProperty('min-width', `${Math.min(640, vw - margin * 2)}px`, 'important');
    picker.style.setProperty('max-width', `${vw - margin * 2}px`, 'important');
    picker.style.setProperty('z-index', '2000', 'important');

    // Horizontal: align to input right edge, clamp into viewport
    let left = rect.right - pickerWidth;
    left = Math.max(margin, Math.min(left, vw - pickerWidth - margin));

    let top: number;
    if (preferDown) {
      top = rect.bottom + gap;
      // If still overflows bottom after max-height, clamp
      if (top + Math.min(picker.offsetHeight || maxHeight, maxHeight) > vh - margin) {
        top = Math.max(margin, vh - margin - maxHeight);
      }
    } else {
      const height = Math.min(picker.offsetHeight || maxHeight, maxHeight);
      top = rect.top - gap - height;
      if (top < margin) {
        top = margin;
      }
    }

    picker.style.setProperty('top', `${Math.round(top)}px`, 'important');
    picker.style.setProperty('left', `${Math.round(left)}px`, 'important');
    picker.style.setProperty('right', 'auto', 'important');
    picker.style.setProperty('bottom', 'auto', 'important');
  }

  
  applyDateRange(event: Event): void {
    let selectedDateRange: { start: string; end: string } | null = null;
    event.preventDefault();
    event.stopPropagation();
    if (this.rangePickerDir && this.rangePickerDir.picker) {
      this.rangePickerDir.picker.clickApply(event);
    };

    selectedDateRange = {
      start: this.formatDate(this.selected.start),
      end: this.formatDate(this.selected.end)
    };
    this.applyRange.emit(selectedDateRange);
    this.pickerOpen = false;
  }

  private formatDate(value: any): string {
    const date = value instanceof Date
      ? value
      : value && typeof value.toDate === 'function'
        ? value.toDate()
        : new Date(value);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  cancelDateRange(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.rangePickerDir && this.rangePickerDir.picker) {
      this.rangePickerDir.picker.clickCancel(event);
    }
    this.pickerOpen = false;
  }

}